import { Box, Typography, makeStyles, Theme } from '@material-ui/core';
import { GetApp } from '@material-ui/icons';
import { useEffect, useMemo } from 'react';
import { Button, PDFPreview, StandardDialog } from '@roc/ui';
import { downloadDocument } from '@roc/feature-utils';

const useStyles = makeStyles((theme: Theme) => ({
  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
    backgroundColor: '#EFEFEF',
  },
  image: {
    maxWidth: '100%',
    maxHeight: '75vh',
    objectFit: 'contain',
    boxShadow: '2px 2px 4px #888',
  },
  unsupported: {
    padding: theme.spacing(6, 2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

export interface UnclassifiedDocumentPreviewData {
  fileName: string;
  contentType: string;
  data: ArrayBuffer;
  headers: Record<string, string>;
}

export interface UnclassifiedDocumentPreviewProps {
  preview: UnclassifiedDocumentPreviewData | null;
  onClose: () => void;
}

const isPdf = (contentType: string, fileName: string) =>
  contentType?.includes('pdf') || fileName?.toLowerCase().endsWith('.pdf');

const isImage = (contentType: string) => contentType?.startsWith('image/');

/**
 * Chat attachments can be PDFs or images, but PDFPreview only renders PDFs — so PDFs go through
 * the shared PDFPreview and images/other files get a plain StandardDialog.
 */
export const UnclassifiedDocumentPreview = ({ preview, onClose }: UnclassifiedDocumentPreviewProps) => {
  const classes = useStyles();
  const open = preview != null;
  const contentType = preview?.contentType;
  const fileName = preview?.fileName;

  const imageUrl = useMemo(() => {
    if (!preview || !isImage(preview.contentType)) return null;
    return URL.createObjectURL(new Blob([preview.data], { type: preview.contentType }));
  }, [preview]);

  useEffect(() => () => {
    if (imageUrl) URL.revokeObjectURL(imageUrl);
  }, [imageUrl]);

  if (open && isPdf(contentType, fileName)) {
    return (
      <PDFPreview
        open
        title={fileName}
        fileName={fileName}
        data={preview.data}
        headers={preview.headers}
        onClose={onClose}
      />
    );
  }

  const handleDownload = () => {
    downloadDocument(preview.data, { ...preview.headers }, 'download', fileName);
  };

  const dialogContent = imageUrl ? (
    <Box className={classes.imageContainer}>
      <img src={imageUrl} alt={fileName} className={classes.image} />
    </Box>
  ) : (
    <Typography className={classes.unsupported}>
      A preview is not available for this file type. Download it to view it.
    </Typography>
  );

  return (
    <StandardDialog
      open={open}
      maxWidth="lg"
      title={fileName}
      handleClose={onClose}
      dialogContent={dialogContent}
      dialogActions={
        <Button
          testId="download-unclassified-document"
          color="primary"
          variant="contained"
          startIcon={<GetApp />}
          onClick={handleDownload}
        >
          Download
        </Button>
      }
    />
  );
};
